/** Route → screen for one pane. The page editor and databases load lazily (Loro, ProseMirror and the database views
 * stay out of the main bundle until a page or board is opened). */
import { Features, Shell, type SettingsSection } from "@bundle/core";
import { Banner, ComingSoon, FEATURE_COPY, Stack, focusMessage, useFeatureAccess, useMail, usePageTreeViewModel, useT, useViewModel } from "@bundle/ui";
import { lazy, Suspense, useEffect, useSyncExternalStore, type ReactNode } from "react";
import { useSidebarView } from "./ActivityBar.tsx";
import { usePlatform } from "./PlatformContext.ts";
import { AgentsScreen } from "../screens/AgentsScreen.tsx";
import { ChannelScreen } from "../screens/ChannelScreen.tsx";
import { DmScreen } from "../screens/DmScreen.tsx";
import { FileScreen } from "../screens/FileScreen.tsx";
import { HomeScreen } from "../screens/HomeScreen.tsx";
import { featureFlags } from "../platform/features.ts";
import { isDesktop } from "../platform/desktop.ts";
import { APP_VERSION } from "../platform/env.ts";
import { DesktopSyncSettings } from "./DesktopSyncSettings.tsx";
import { ActivityScreen } from "../screens/ActivityScreen.tsx";
import { SearchScreen } from "../screens/SearchScreen.tsx";
import { LaterScreen } from "../screens/LaterScreen.tsx";
import { UnreadsScreen } from "../screens/UnreadsScreen.tsx";
import { ScheduledScreen } from "../screens/ScheduledScreen.tsx";
import { DraftsScreen } from "../screens/DraftsScreen.tsx";
import { ProjectsScreen } from "../screens/ProjectsScreen.tsx";
import { ThreadScreen } from "../screens/ThreadScreen.tsx";
import { ContactsScreen } from "../screens/mail/ContactsScreen.tsx";
import { MailScreen } from "../screens/mail/MailScreen.tsx";
import { MailThreadScreen } from "../screens/mail/MailThreadScreen.tsx";
import { DocsScreen } from "../screens/DocsScreen.tsx";

const CalendarScreen = lazy(() => import("../screens/calendar/CalendarScreen.tsx").then((m) => ({ default: m.CalendarScreen })));

const PageScreen = lazy(() => import("../screens/PageScreen.tsx").then((m) => ({ default: m.PageScreen })));
const HuddleScreen = lazy(() => import("../screens/HuddleScreen.tsx").then((m) => ({ default: m.HuddleScreen })));
const FilesScreen = lazy(() => import("../screens/files/FilesScreen.tsx").then((m) => ({ default: m.FilesScreen })));
const HuddlesScreen = lazy(() => import("../screens/HuddlesScreen.tsx").then((m) => ({ default: m.HuddlesScreen })));
const SettingsScreen = lazy(() => import("../screens/SettingsScreen.tsx").then((m) => ({ default: m.SettingsScreen })));
const DatabaseScreen = lazy(() => import("../screens/DatabaseScreen.tsx").then((m) => ({ default: m.DatabaseScreen })));

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Stack grow testID="pane-loading" />}>{children}</Suspense>;
}

/** /p/{id}: databases render as databases; pages and rows open the editor. */
function PageRoute({ pageId }: { pageId: string }) {
  const tree = usePageTreeViewModel();
  const kind = useViewModel(tree).cache?.pages[pageId]?.kind;
  return <Lazy>{kind === "database" ? <DatabaseScreen key={pageId} databaseId={pageId} /> : <PageScreen key={pageId} pageId={pageId} />}</Lazy>;
}

/** Settings, with the usage-analytics consent switch where analytics exist (Preferences → Privacy). */
function SettingsPane({ section }: { section: SettingsSection }) {
  const analytics = usePlatform().analytics;
  const status = useSyncExternalStore(analytics.subscribe, analytics.getSnapshot, analytics.getSnapshot);
  const preference = { granted: status.collecting, byDefault: status.setting === "onByDefault", onChange: (on: boolean) => analytics.setConsent(on) };
  const device = { desktop: isDesktop(), version: APP_VERSION, syncPage: <DesktopSyncSettings /> };
  return <SettingsScreen section={section} device={device} {...(analytics.available ? { analytics: preference } : {})} />;
}

/**
 * A switched-off product area (core logic/features): a placeholder instead of the screen (links, history, shortcuts),
 * or, for owners and admins the workspace lets look, the screen under a read-only banner (the API refuses changes).
 */
export function PaneView({ url }: { url: string }) {
  const t = useT();
  const access = useFeatureAccess();
  const r = Shell.parseUrl(url);
  const area = r === undefined ? undefined : Features.featureOfRoute(r);
  const level = area === undefined ? "full" : access(area);
  if (area !== undefined && level === "none") {
    const name = t(FEATURE_COPY[area].title);
    return <ComingSoon icon="ban" title={t("features.disabled.title", { area: name })} body={t("features.disabled.body", { area: name })} />;
  }
  const screen = <PaneScreen url={url} />;
  if (area === undefined || level === "full") return screen;
  return (
    <Stack grow>
      <Banner tone="warning" message={t("features.readOnly", { area: t(FEATURE_COPY[area].title) })} testID="feature-read-only" />
      {screen}
    </Stack>
  );
}

function PaneScreen({ url }: { url: string }) {
  const t = useT();
  const r = Shell.parseUrl(url);
  // Mail, Contacts and Calendar exist only where the server turned them on (MAIL_WORKSPACES → bootstrap `mail`).
  const mailOn = useMail() !== undefined;
  // The Email view shows the mail folders in the sidebar: the mail screen then hides its own rail.
  const sidebarView = useSidebarView();
  const mailRailInSidebar = sidebarView?.view === "email" && sidebarView.sidebarOpen;
  // The Contacts view's sidebar lists the kinds and Add contact: the screen then hides its own.
  const contactsInSidebar = sidebarView?.view === "contacts" && sidebarView.sidebarOpen;
  // Message links (`?m=`) in a thread: scroll to the reply and flash it once the thread shows it (a channel's
  // ChannelScreen first loads older history around the message when it isn't loaded yet).
  const focus = r !== undefined && r.type === "thread" ? r.focus : undefined;
  useEffect(() => {
    if (focus !== undefined) focusMessage(focus);
  }, [focus, url]);
  if (r === undefined) return <ComingSoon icon="globe" title={url} body={t("shell.omnibox.noResults")} />;
  if (!mailOn && (r.type === "mail" || r.type === "mailThread" || r.type === "contacts" || r.type === "calendar")) {
    return <ComingSoon icon="globe" title={url} body={t("shell.omnibox.noResults")} />;
  }
  switch (r.type) {
    case "home":
      return <HomeScreen />;
    case "channel":
      return <ChannelScreen key={r.channelId} channelId={r.channelId} {...(r.focus === undefined ? {} : { focus: r.focus })} />;
    case "dm":
      return <DmScreen key={r.userId} userId={r.userId} />;
    case "thread":
      return <ThreadScreen key={r.rootId} channelId={r.channelId} rootId={r.rootId} />;
    case "agents":
      return <AgentsScreen />;
    case "projects":
      return <ProjectsScreen />;
    case "search":
      return <SearchScreen query={r.query} />;
    case "activity":
      return <ActivityScreen />;
    case "later":
      return <LaterScreen />;
    case "mail":
      return <MailScreen mailboxId={r.mailboxId} group={r.group} folder={r.folder} tab={r.tab} label={r.label} q={r.q} filter={r.filter} railInSidebar={mailRailInSidebar} />;
    case "mailThread":
      return <MailThreadScreen key={r.threadId} threadId={r.threadId} />;
    case "contacts":
      return <ContactsScreen kind={r.kind} contactId={r.contactId} filterInSidebar={contactsInSidebar} />;
    case "docs":
      return <DocsScreen />;
    case "calendar":
      return (
        <Lazy>
          <CalendarScreen view={r.view} at={r.at} event={r.event} />
        </Lazy>
      );
    case "unreads":
      return <UnreadsScreen />;
    case "scheduled":
      return <ScheduledScreen />;
    case "drafts":
      return <DraftsScreen />;
    case "huddle":
      return featureFlags.huddles ? (
        <Lazy>
          <HuddleScreen key={r.huddleId} channelId={r.huddleId} />
        </Lazy>
      ) : (
        <ComingSoon icon="huddle" title={t("huddle.title")} body={t("huddle.comingSoon")} />
      );
    case "huddles":
      return featureFlags.huddles ? (
        <Lazy>
          <HuddlesScreen key={r.huddleId ?? ""} {...(r.huddleId === undefined ? {} : { huddleId: r.huddleId })} />
        </Lazy>
      ) : (
        <ComingSoon icon="huddle" title={t("nav.huddles")} body={t("huddle.comingSoon")} />
      );
    case "file":
      return <FileScreen key={r.fileId} fileId={r.fileId} />;
    case "files":
      return (
        <Lazy>
          <FilesScreen route={r} />
        </Lazy>
      );
    case "settings":
      // Settings ship behind a flag (platform/features.ts); the screen itself loads lazily.
      return featureFlags.settings ? (
        <Lazy>
          <SettingsPane section={r.section} />
        </Lazy>
      ) : (
        <ComingSoon icon="settings" title={t("settings.title")} body={t("settings.comingSoon")} />
      );
    case "page":
      return <PageRoute pageId={r.pageId} />;
    case "db":
      return (
        <Lazy>
          <DatabaseScreen key={r.dbId} databaseId={r.dbId} viewId={r.view} />
        </Lazy>
      );
  }
}
